/* Approvals & Actions — the founder's decision desk. The approvals queue tops
   the surface (every pending [I DO] with Approve / Deny), followed by the
   high-value actions grid (deploy · rollback · QA sweep · health-check · run
   agent · flags jump · pause/resume crew · maintenance · pause spend · silence
   alert), live feature flags, and the append-only audit table. EVERY decision
   and action goes through askConfirm + an audit row — no exceptions. */
import { useRef, useState, type ReactNode } from 'react';
import { Button, Switch, Table } from '../../ds';
import { useContract } from '../../lib/api';
import { useOps, type ConfirmConfig } from '../ops';
import { Widget, Panel, Kicker } from '../ui';
import { WidgetBody } from '../../ds/mc-kit';
import { tone as toneColor, toneSoft, toneLine } from '../../lib/selectors';
import { integrationFor } from '../../lib/flags';
import type { ApprovalItem } from '../../lib/contract';

/* ---------- icons (line, Lucide-style — no emoji) ---------- */
const svg = (d: string) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={d} />
  </svg>
);
const ACTION_ICON: Record<string, ReactNode> = {
  deploy: svg('M5 13c-1.5 1.3-2 5-2 5s3.7-.5 5-2M9 11a8 8 0 0 1 7-7c3 0 4 1 4 4a8 8 0 0 1-7 7l-2 .5L8.5 13z'),
  rollback: svg('M3 4v6h6M21 12a9 9 0 1 1-9-9c2.52 0 4.85 1 6.55 2.75L21 8'),
  qa: svg('M9 3h6v3H9zM15 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2M9 14l2 2 4-4'),
  health: svg('M3 12h4l2-6 4 12 2-6h6'),
  run: svg('M12 3v4M12 17v4M3 12h4M17 12h4M6.3 6.3l2.4 2.4M15.3 15.3l2.4 2.4M17.7 6.3l-2.4 2.4M8.7 15.3l-2.4 2.4'),
  flag: svg('M4 21V4M4 4h13l-2 4 2 4H4'),
  'pause-crew': svg('M9 5v14M15 5v14'),
  'resume-crew': svg('M7 5l12 7-12 7z'),
  maintenance: svg('M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z'),
  'pause-spend': svg('M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6'),
  silence: svg('M8.7 4.3A6 6 0 0 1 18 8c0 3 1 5 1 5M6 8c0 5-3 7-3 7h13M13.7 21a2 2 0 0 1-3.4 0M3 3l18 18'),
};

/** Fire a server-side executor. Throws on failure so the confirm flow records
    the audit row with result:'error'. Unconfigured executors stub-accept (200). */
async function execAction(action: string): Promise<void> {
  const r = await fetch(`/api/controls/${action}`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: '{}' });
  if (!r.ok) throw new Error(`${action} failed (${r.status})`);
}

/* ---------- approvals: kind chips + decision pills ---------- */
/* Chip tone: spend/token = warn (money + secrets want attention), deploy/merge
   = iris (ship actions), urgency high overrides to danger. Color is never the
   only signal — the chip always carries a dot AND the kind word, plus the
   literal word "urgent" when urgency is high. */
const KIND_TONE: Record<string, string> = { deploy: 'iris', merge: 'iris', token: 'warn', spend: 'warn', other: 'muted' };

function KindChip({ kind, urgency }: { kind: string; urgency: string }) {
  const t = urgency === 'high' ? 'danger' : (KIND_TONE[kind] ?? 'muted');
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flex: 'none', fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: toneColor(t), background: toneSoft(t), border: `1px solid ${toneLine(t)}`, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap' }}>
      <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: toneColor(t) }} />
      {kind}
      {urgency === 'high' && ' · urgent'}
    </span>
  );
}

function DecisionPill({ status }: { status: string }) {
  const approved = status === 'approved';
  const c = approved ? 'var(--accent-ink)' : 'var(--danger)';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flex: 'none', fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: c, background: approved ? 'var(--accent-soft)' : 'var(--danger-soft)', border: `1px solid ${approved ? 'var(--accent-line)' : 'var(--danger-line)'}`, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap' }}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d={approved ? 'M20 6 9 17l-5-5' : 'M18 6 6 18M6 6l12 12'} />
      </svg>
      {status}
    </span>
  );
}

/* Cyan check-pill for audit result cells (danger x for errors). */
function ResultPill({ result }: { result: string }) {
  const bad = result === 'error';
  const c = bad ? 'var(--danger)' : 'var(--accent-ink)';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: c, background: bad ? 'var(--danger-soft)' : 'var(--accent-soft)', border: `1px solid ${bad ? 'var(--danger-line)' : 'var(--accent-line)'}`, padding: '2px 8px', borderRadius: 999 }}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d={bad ? 'M18 6 6 18M6 6l12 12' : 'M20 6 9 17l-5-5'} />
      </svg>
      {result}
    </span>
  );
}

/* ---------- feature flags (client state, persisted per browser) ---------- */
const FLAG_DEFS = [
  { key: 'trailquest.badges', desc: 'Badge shelf live on TrailQuest', init: true },
  { key: 'site.banner', desc: 'Launch banner on carrtech.ai', init: false },
  { key: 'crew.autopilot', desc: 'Crew may ship low-risk PRs unattended', init: true },
  { key: 'dispatch.forge', desc: 'Dispatch bridge may hand tasks to Forge', init: false },
  { key: 'chat.live', desc: 'Talk-to-AI dock uses the live Anthropic route', init: false },
  { key: 'pwa.nudge', desc: 'Show the add-to-home-screen nudge on mobile', init: false },
];
const FLAGS_KEY = 'mc.flags';
function loadFlags(): Record<string, boolean> {
  const base = Object.fromEntries(FLAG_DEFS.map((f) => [f.key, f.init]));
  try {
    const raw = localStorage.getItem(FLAGS_KEY);
    if (raw) return { ...base, ...(JSON.parse(raw) as Record<string, boolean>) };
  } catch {
    /* corrupt/absent storage — fall through to defaults */
  }
  return base;
}

const metaMono: React.CSSProperties = { fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--muted)' };
const ell: React.CSSProperties = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

interface ActionCard { id: string; label: string; desc: string; tone: string; foot: string; onClick: () => void }

export function Controls() {
  const { askConfirm, optimisticAudit } = useOps();
  const approvals = useContract('approvals', { pollMs: 15_000 });
  const audit = useContract('audit', { pollMs: 30_000 });
  const [flags, setFlags] = useState<Record<string, boolean>>(loadFlags);
  const [crewPaused, setCrewPaused] = useState(false);
  const flagsRef = useRef<HTMLDivElement>(null);

  /* ---- approvals: every decision = confirm dialog + audit row ---- */
  const items = approvals.data ?? [];
  const pending = items.filter((a) => a.status === 'pending');
  const decided = items.filter((a) => a.status !== 'pending');

  const decide = (a: ApprovalItem, decision: 'approved' | 'denied') => {
    const approving = decision === 'approved';
    askConfirm({
      title: approving ? 'Approve this request?' : 'Deny this request?',
      body: approving
        ? `${a.requestedBy} asked for: ${a.title}. Approving unblocks it immediately — ${a.detail}`
        : `This blocks “${a.title}”. ${a.requestedBy} gets a denial and the ${a.kind} request stays parked until someone re-requests it.`,
      confirmLabel: approving ? 'Approve' : 'Deny',
      tone: approving ? 'primary' : 'danger',
      auditAction: approving ? 'Approved' : 'Denied',
      auditTarget: a.title,
      toastTitle: approving ? 'Approved' : 'Denied',
      toastMsg: a.title,
      toastTone: approving ? 'success' : 'warn',
      run: async () => {
        const r = await fetch('/api/approvals', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ op: 'decide', id: a.id, decision }),
        });
        if (!r.ok) throw new Error(`decide → ${r.status}`);
        approvals.refetch();
      },
    });
  };

  /* ---- flags: each toggle = confirm dialog + audit row, then persist ---- */
  const toggleFlag = (key: string) => {
    const next = !flags[key];
    askConfirm({
      title: `Turn ${key} ${next ? 'on' : 'off'}?`,
      body: `Flips the ${key} flag ${next ? 'on' : 'off'}. Client-side flag — it takes effect immediately in this browser and is logged to the audit trail.`,
      confirmLabel: next ? 'Turn on' : 'Turn off',
      tone: 'primary',
      auditAction: 'Toggled flag',
      auditTarget: `${key} = ${next ? 'on' : 'off'}`,
      toastTitle: 'Flag toggled',
      toastMsg: `${key} → ${next ? 'on' : 'off'}`,
      toastTone: 'info',
      run: async () => {
        setFlags((f) => {
          const updated = { ...f, [key]: next };
          try { localStorage.setItem(FLAGS_KEY, JSON.stringify(updated)); } catch { /* private mode */ }
          return updated;
        });
      },
    });
  };

  const jumpToFlags = () => {
    flagsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    flagsRef.current?.querySelector('input')?.focus({ preventScroll: true });
  };

  /* ---- high-value actions (every one = confirm + POST + audit) ---- */
  const act = (cfg: ConfirmConfig) => () => askConfirm(cfg);
  const cards: ActionCard[] = [
    { id: 'deploy', label: 'Trigger deploy', desc: 'Ship carrtech-web to production', tone: 'iris', foot: 'requires confirm', onClick: act({ title: 'Deploy to production?', body: 'This ships carrtech-web to production via the Cloudflare deploy hook. The crew built it — you sign it off.', confirmLabel: 'Deploy', tone: 'primary', auditAction: 'Deployed', auditTarget: 'carrtech-web → prod', toastTitle: 'Deploying', toastMsg: 'carrtech-web → production', toastTone: 'success', run: () => execAction('deploy') }) },
    { id: 'rollback', label: 'Rollback last deploy', desc: 'Return production to the previous build', tone: 'warn', foot: 'requires confirm', onClick: act({ title: 'Roll back production?', body: 'Production returns to the previous successful deployment. The current build stays in history and can be re-promoted.', confirmLabel: 'Roll back', tone: 'danger', auditAction: 'Rolled back', auditTarget: 'production → previous deploy', toastTitle: 'Rolling back', toastMsg: 'production → previous deploy', toastTone: 'warn', run: () => execAction('rollback') }) },
    { id: 'qa', label: 'Run QA sweep', desc: 'Typecheck, build, axe and overflow checks', tone: 'cyan', foot: 'requires confirm', onClick: act({ title: 'Run the QA sweep?', body: 'Kicks off the full portal QA sweep — typecheck, build, accessibility and 390px overflow checks. Results land in the activity feed.', confirmLabel: 'Run sweep', tone: 'primary', auditAction: 'Ran QA sweep', auditTarget: 'portal · full sweep', toastTitle: 'QA sweep', toastMsg: 'Sweep queued', toastTone: 'info', run: () => execAction('qa') }) },
    { id: 'health', label: 'Health-check', desc: 'Ping every system now', tone: 'cyan', foot: 'requires confirm', onClick: act({ title: 'Run a health-check?', body: 'Ping Site, Workers, CI, Sentry and the Anthropic API, then refresh the ribbon.', confirmLabel: 'Run check', tone: 'primary', auditAction: 'Health-check', auditTarget: 'all systems', toastTitle: 'Health-check', toastMsg: 'Pinging all systems…', toastTone: 'info', run: () => execAction('health') }) },
    { id: 'run', label: 'Run agent', desc: 'Brief and launch an agent on a task', tone: 'iris', foot: 'requires confirm', onClick: act({ title: 'Run an agent?', body: 'Launch Forge on an ad-hoc task via workflow dispatch. It will burn tokens against the current budget.', confirmLabel: 'Run agent', tone: 'primary', auditAction: 'Ran agent', auditTarget: 'Forge · ad-hoc task', toastTitle: 'Agent running', toastMsg: 'Forge picked up the task', toastTone: 'success', run: () => execAction('run') }) },
    { id: 'flag', label: 'Toggle feature flag', desc: 'Flip a flag in the panel below', tone: 'cyan', foot: 'jumps to flags', onClick: jumpToFlags },
    crewPaused
      ? { id: 'resume-crew', label: 'Resume crew', desc: 'Let the agents pick up queued work again', tone: 'cyan', foot: 'requires confirm', onClick: act({ title: 'Resume the crew?', body: 'Agents resume picking up queued tasks and running briefs. Anything parked while paused starts moving again.', confirmLabel: 'Resume crew', tone: 'primary', auditAction: 'Resumed crew', auditTarget: 'all agents', toastTitle: 'Crew resumed', toastMsg: 'Agents are picking up work', toastTone: 'success', run: async () => { await execAction('resume-crew'); setCrewPaused(false); } }) }
      : { id: 'pause-crew', label: 'Pause crew', desc: 'Stop agents picking up new work', tone: 'warn', foot: 'requires confirm', onClick: act({ title: 'Pause the crew?', body: 'Agents finish their current step, then stop picking up new tasks until you resume. Nothing in flight is killed.', confirmLabel: 'Pause crew', tone: 'primary', auditAction: 'Paused crew', auditTarget: 'all agents', toastTitle: 'Crew paused', toastMsg: 'No new tasks will start', toastTone: 'warn', run: async () => { await execAction('pause-crew'); setCrewPaused(true); } }) },
    { id: 'maintenance', label: 'Maintenance mode', desc: 'Put the site behind a maintenance page', tone: 'danger', foot: 'requires confirm', onClick: act({ title: 'Enter maintenance mode?', body: 'Visitors to carrtech.ai see the maintenance page instead of the site until you turn it off. Use this only during a real incident.', confirmLabel: 'Enter maintenance', tone: 'danger', auditAction: 'Enabled maintenance mode', auditTarget: 'carrtech.ai → maintenance page', toastTitle: 'Maintenance mode', toastMsg: 'carrtech.ai → maintenance page', toastTone: 'warn', run: () => execAction('maintenance') }) },
    { id: 'pause-spend', label: 'Pause spend', desc: 'Freeze all AI token spend — the cost guardrail', tone: 'danger', foot: 'requires confirm', onClick: act({ title: 'Pause all AI spend?', body: 'Every agent stops making paid API calls immediately. Work in progress halts mid-task until you unfreeze spend — this is the emergency cost brake.', confirmLabel: 'Pause spend', tone: 'danger', auditAction: 'Paused spend', auditTarget: 'Anthropic · all agents', toastTitle: 'Spend paused', toastMsg: 'All paid API calls frozen', toastTone: 'warn', run: () => execAction('pause-spend') }) },
    { id: 'silence', label: 'Silence alert', desc: 'Mute the active red alert for 24h', tone: 'warn', foot: 'requires confirm', onClick: act({ title: 'Silence this alert?', body: 'Mute the highest active alert for 24h. It clears the red-alert ribbon from Command but the underlying issue stays open.', confirmLabel: 'Silence', tone: 'danger', auditAction: 'Silenced alert', auditTarget: 'highest active alert · 24h', toastTitle: 'Alert silenced', toastMsg: 'Muted for 24h', toastTone: 'warn', run: () => execAction('silence') }) },
  ];

  /* ---- audit table (server rows + optimistic) ---- */
  const serverRows = audit.data ?? [];
  const rows = [...optimisticAudit, ...serverRows];
  const auditColumns = [
    { key: 'action', label: 'action' },
    { key: 'target', label: 'target' },
    { key: 'result', label: 'result' },
    { key: 'when', label: 'when', align: 'right' as const },
  ];
  const auditRows = rows.map((r, i) => ({
    key: i,
    action: r.action,
    target: r.target,
    result: <ResultPill result={r.result} />,
    when: r.time,
  }));

  return (
    <div data-screen-label="Controls" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--mc-gap)' }}>

      {/* === APPROVALS QUEUE — the heartbeat, always on top === */}
      <Widget
        query={approvals}
        title="// approvals"
        accent
        label="Approvals queue"
        minHeight={160}
        rows={3}
        emptyLabel="Nothing waiting on you."
        emptySub="Requests from the crew — deploys, merges, tokens, spend — land here."
        errorLabel="Approvals unavailable"
        errorSub="Couldn’t reach the queue."
        connect={integrationFor('approvals')?.connect}
        badge="connect me"
        aside={pending.length > 0 ? <Kicker style={{ color: 'var(--primary)' }}>{pending.length} pending</Kicker> : undefined}
      >
        {() => (
          <div>
            {pending.length === 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--muted)', fontSize: 13.5, padding: '6px 2px' }}>
                <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', flex: 'none' }} />
                Nothing waiting on you.
              </div>
            ) : (
              <div role="list" aria-label={`${pending.length} pending approvals`}>
                {pending.map((a) => (
                  <div key={a.id} role="listitem" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, padding: '13px 0', borderBottom: '1px solid var(--border)' }}>
                    <span aria-hidden="true" style={{ width: 32, height: 32, flex: 'none', borderRadius: 8, display: 'grid', placeItems: 'center', fontSize: 15, background: 'var(--surface2)', border: '1px solid var(--border)', alignSelf: 'flex-start', marginTop: 2 }}>
                      {a.glyph || <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--muted)' }} />}
                    </span>
                    <div style={{ flex: 1, minWidth: 210 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <KindChip kind={a.kind} urgency={a.urgency} />
                        <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 14, color: 'var(--ink)', minWidth: 0 }}>{a.title}</span>
                      </div>
                      <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 4, lineHeight: 1.5 }}>{a.detail}</div>
                      <div style={{ ...metaMono, marginTop: 5 }}>requested by {a.requestedBy} · {a.time} ago</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flex: 'none', marginLeft: 'auto' }}>
                      <Button size="sm" variant="danger" style={{ minHeight: 44 }} aria-label={`Deny: ${a.title}`} onClick={() => decide(a, 'denied')}>Deny</Button>
                      <Button size="sm" variant="primary" style={{ minHeight: 44 }} aria-label={`Approve: ${a.title}`} onClick={() => decide(a, 'approved')}>Approve</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {decided.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Kicker>recent decisions</Kicker>
                <div role="list" aria-label="Recent decisions" style={{ marginTop: 6 }}>
                  {decided.map((a) => (
                    <div key={a.id} role="listitem" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                      <DecisionPill status={a.status} />
                      <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: 'var(--ink)', ...ell }}>{a.title}</span>
                      <span style={{ ...metaMono, flex: 'none' }}>{a.requestedBy} · {a.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Widget>

      {/* === HIGH-VALUE ACTIONS — bare section, buttons ARE the cards === */}
      <section aria-label="High-value actions">
        <div style={{ marginBottom: 12 }}>
          <Kicker>// actions</Kicker>
        </div>
        <div className="mc-auto mc-auto-md">
          {cards.map((a) => (
            <button key={a.id} onClick={a.onClick}
              style={{ textAlign: 'left', background: 'var(--surface)', border: `1px solid ${a.tone === 'danger' ? 'var(--danger-line)' : 'var(--border)'}`, borderRadius: 'var(--radius-lg)', padding: 18, display: 'flex', flexDirection: 'column', gap: 12, cursor: 'pointer', transition: 'border-color var(--dur) ease, transform var(--dur) ease' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = a.tone === 'danger' ? 'var(--danger)' : 'var(--primary-line)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = a.tone === 'danger' ? 'var(--danger-line)' : 'var(--border)'; (e.currentTarget as HTMLElement).style.transform = ''; }}>
              <span style={{ width: 42, height: 42, borderRadius: 'var(--radius-md)', display: 'grid', placeItems: 'center', color: toneColor(a.tone), background: 'var(--surface2)', border: '1px solid var(--border)' }}>
                {ACTION_ICON[a.id]}
              </span>
              <div>
                <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 15, color: 'var(--ink)' }}>{a.label}</div>
                <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 3, lineHeight: 1.45 }}>{a.desc}</div>
              </div>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-mono)', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--muted)' }}>
                {a.foot}
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* === FLAGS (1fr) + AUDIT (1.5fr) === */}
      <div className="mc-row-2" style={{ '--mc-2': '1fr 1.5fr' } as React.CSSProperties}>
        <div ref={flagsRef} style={{ minWidth: 0, scrollMarginTop: 16 }}>
          <Panel title="// flags" label="Feature flags" aside={<Kicker>confirmed + audited</Kicker>}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {FLAG_DEFS.map((f) => (
                <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--ink)', ...ell }}>{f.key}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2, lineHeight: 1.4 }}>{f.desc}</div>
                  </div>
                  <Switch checked={flags[f.key]} onChange={() => toggleFlag(f.key)} label={f.key} />
                </div>
              ))}
            </div>
          </Panel>
        </div>

        <Panel title="// audit" label="Audit log" aside={<Kicker>append-only</Kicker>}>
          <WidgetBody state={audit.state === 'live' && rows.length === 0 ? 'empty' : audit.state} rows={4} minHeight={180}
            emptyLabel="No actions logged" errorLabel="Audit unavailable" onRetry={audit.refetch}>
            <div className="mc-table-wrap" tabIndex={0} role="region" aria-label="Audit log table (scrollable)"><Table columns={auditColumns} rows={auditRows} /></div>
          </WidgetBody>
        </Panel>
      </div>
    </div>
  );
}
